// =============================================================================
// terminal.ts — staff calls to booking-terminal (WisePOS E via Stripe Terminal)
// =============================================================================
// The reader is never chosen client-side: booking-terminal resolves the
// booking's location → infra.location.pos_reader_id. Same fetch-helper shape
// as walkin.ts (session JWT → Authorization: Bearer).
// =============================================================================
import { BOOKING_API_URL, supabase } from '../lib/supabase';

export interface TerminalReaderState {
  configured: boolean;
  id: string | null;
  label: string | null;
  status: string | null;      // 'online' | 'offline' | 'missing' (deleted in Stripe) | 'unknown' | null
  busy: boolean;              // reader is processing a DIFFERENT payment
}

// A reader that cannot take a payment right now. 'unknown' (transient probe
// error) is deliberately NOT blocking — a Stripe hiccup must not stop sales.
export function readerUnusable(state: TerminalReaderState | null): boolean {
  return state?.status === 'offline' || state?.status === 'missing';
}

export function readerProblemText(state: TerminalReaderState | null): string | null {
  if (!state) return null;
  const name = state.label ?? state.id ?? 'Pinterminal';
  if (state.status === 'offline') return `Pinterminal ${name} is offline (uit, of geen internet).`;
  if (state.status === 'missing') return `Pinterminal ${name} bestaat niet meer in Stripe — kies een andere terminal bij Setup → Locaties.`;
  return null;
}

export interface TerminalQuote {
  booking_number: string;
  amount_due_cents: number;
  currency: string;
  payment_status: string;
  lifecycle_status: string;
  payable: boolean;
  reader: TerminalReaderState;
}

export interface TerminalStart {
  started: true;
  payment_intent_id: string;
  reader_id: string;
  reader_label: string | null;
  amount_due_cents: number;
  currency: string;
  booking_number: string;
  checkin_departure_id: string | null;
}

export interface TerminalStatus {
  booking_payment_status: string;
  booking_lifecycle_status: string;
  pi_status: string | null;
  reader_action_status: 'in_progress' | 'succeeded' | 'failed' | null;
  reader_action_failure_code: string | null;
  reader_action_failure_message: string | null;
  amount_due_cents: number;
  checked_in: boolean | null;
}

export interface TerminalReader {
  id: string;
  label: string | null;
  status: string | null;
  device_type: string | null;
  location: string | null;
  serial_number: string | null;
}

export interface TerminalError {
  error: string;
  message?: string;
  code?: string | null;
  pi_status?: string;
  payment_status?: string;
  lifecycle?: string;
}

export interface TerminalResult<T> {
  ok: boolean;
  data: T | null;
  error: TerminalError | null;
  status: number;
}

export async function call<T>(
  body: Record<string, unknown>,
  fn = 'booking-terminal',
): Promise<TerminalResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as TerminalError) ?? { error: 'unknown_error', message: `HTTP ${res.status}` },
      status: res.status,
    };
  }
  return { ok: true, data: json as T, error: null, status: res.status };
}

export function terminalQuote(bookingId: string) {
  return call<TerminalQuote>({ action: 'quote', booking_id: bookingId });
}

// `provisional` marks a booking that exists only to carry this payment (the
// walk-in modal just created it), so an abandoned payment may take the booking
// with it. Never set it for a booking that already existed on its own.
export function terminalStart(
  bookingId: string,
  checkinDepartureId?: string | null,
  provisional = false,
) {
  return call<TerminalStart>({
    action: 'start',
    booking_id: bookingId,
    ...(checkinDepartureId ? { checkin_departure_id: checkinDepartureId } : {}),
    ...(provisional ? { provisional: true } : {}),
  });
}

export function terminalStatus(bookingId: string, checkinDepartureId?: string | null) {
  return call<TerminalStatus>({
    action: 'status',
    booking_id: bookingId,
    ...(checkinDepartureId ? { checkin_departure_id: checkinDepartureId } : {}),
  });
}

export function terminalRetry(bookingId: string) {
  return call<{ processing: boolean }>({ action: 'retry', booking_id: bookingId });
}

export function terminalCancel(bookingId: string) {
  return call<{ canceled: boolean; reason?: string }>({ action: 'cancel', booking_id: bookingId });
}

// "Anders afrekenen": settle this booking another way (cash, betaallink).
// Frees the reader AND pins the booking so the POS sweeper never cancels it.
export function terminalKeepUnpaid(bookingId: string) {
  return call<{ kept_unpaid: boolean }>({ action: 'keep_unpaid', booking_id: bookingId });
}

export function terminalListReaders() {
  return call<{ readers: TerminalReader[]; mode: string }>({ action: 'list_readers' });
}

// Live state of a location's reader, without a booking (walk-in modal
// payment step): offline/busy is known before the booking is created.
export function terminalReaderStatus(locationId: string) {
  return call<TerminalReaderState>({ action: 'reader_status', location_id: locationId });
}

// -----------------------------------------------------------------------------
// ChargeDriver — what TerminalChargePanel needs from "a payment on the
// WisePOS". The booking driver wraps booking-terminal; the gift card load
// driver (api/giftcard.ts) wraps booking-giftcard. `settled` is the panel's
// success signal (Stripe succeeded, or the server already recorded it).
// -----------------------------------------------------------------------------
export interface ChargeStart {
  amount_due_cents: number;
  reader_id: string;
  reader_label: string | null;
}

export interface ChargeStatus {
  settled: boolean;
  pi_status: string | null;
  reader_action_status: 'in_progress' | 'succeeded' | 'failed' | null;
  reader_action_failure_code: string | null;
  reader_action_failure_message: string | null;
  amount_due_cents: number | null;
  checked_in: boolean | null;
}

export interface ChargeDriver {
  start: () => Promise<TerminalResult<ChargeStart>>;
  status: () => Promise<TerminalResult<ChargeStatus>>;
  retry: () => Promise<TerminalResult<unknown>>;
  cancel: () => Promise<TerminalResult<{ canceled: boolean; reason?: string }>>;
  // "Anders afrekenen" (bookings only): free the reader, keep the booking unpaid.
  keepUnpaid?: () => Promise<TerminalResult<unknown>>;
}

export function bookingChargeDriver(
  bookingId: string,
  checkinDepartureId: string | null,
  provisional: boolean,
): ChargeDriver {
  return {
    start: () => terminalStart(bookingId, checkinDepartureId, provisional),
    status: async () => {
      const res = await terminalStatus(bookingId, checkinDepartureId);
      if (!res.ok || !res.data) return { ...res, data: null };
      const s = res.data;
      return {
        ...res,
        data: {
          settled: s.booking_payment_status === 'paid' || s.pi_status === 'succeeded',
          pi_status: s.pi_status,
          reader_action_status: s.reader_action_status,
          reader_action_failure_code: s.reader_action_failure_code,
          reader_action_failure_message: s.reader_action_failure_message,
          amount_due_cents: s.amount_due_cents,
          checked_in: s.checked_in,
        },
      };
    },
    retry: () => terminalRetry(bookingId),
    cancel: () => terminalCancel(bookingId),
    keepUnpaid: () => terminalKeepUnpaid(bookingId),
  };
}

// Dutch copy for the error codes booking-terminal returns.
export function terminalErrorMessage(err: TerminalError | null): string {
  switch (err?.error) {
    case 'no_reader_configured':
      return 'Geen pinterminal ingesteld voor deze locatie (Setup → Locaties).';
    case 'reader_busy':
      return 'De pinterminal is bezet met een andere betaling. Probeer zo opnieuw.';
    case 'reader_unavailable':
      // Stripe's messages are long integration-guide prose; map the known
      // codes to something a counter can act on.
      if (err?.code === 'terminal_reader_offline') {
        return 'De pinterminal is offline (uit, of geen internet). Zet hem aan / check de wifi en probeer opnieuw.';
      }
      if (err?.code === 'terminal_reader_timeout') {
        return 'De pinterminal reageert niet. Controleer de verbinding en probeer opnieuw.';
      }
      return `De pinterminal is niet beschikbaar${err?.message ? ` (${err.message.split('.')[0]})` : ''}.`;
    case 'payment_in_progress':
      return 'Er loopt al een betaling voor deze boeking.';
    case 'not_payable':
      return 'Deze boeking staat niet meer open voor betaling.';
    case 'nothing_due':
      return 'Er staat geen bedrag open.';
    case 'booking_changed':
      return 'De boeking is intussen gewijzigd (al betaald?). Ververs de pagina.';
    case 'invalid_lifecycle':
      return 'Deze boeking kan in deze status niet afgerekend worden.';
    case 'staff_only':
    case 'not_an_operator':
    case 'unauthenticated':
      return 'Geen geldige operator-sessie. Log opnieuw in.';
    case 'network_error':
      return 'Geen verbinding met de server.';
    // booking-giftcard
    case 'gift_card_not_found':
      return 'Deze cadeaubon bestaat niet.';
    case 'card_blocked':
      return 'Deze cadeaubon is geblokkeerd.';
    case 'amount_invalid':
      return 'Kies een bedrag tussen € 5 en € 250.';
    case 'balance_cap':
      return 'Er mag maximaal € 500 op een cadeaubon staan.';
    case 'location_not_allowed':
      return 'Je mag op deze locatie geen cadeaubonnen verkopen.';
    case 'location_invalid':
      return 'Kies eerst een locatie.';
    case 'not_authorized':
      return 'Je hebt geen recht om cadeaubonnen te verkopen.';
    case 'load_in_progress':
      return 'Er loopt al een opwaardering voor deze kaart.';
    default:
      return err?.message ? `${err.error}: ${err.message}` : (err?.error ?? 'Onbekende fout.');
  }
}
